import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from '@material-ui/core'
import { useEffect, useState } from 'react'

import { Form, Formik } from 'formik'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { InputField } from '../InputField'
import { InputSwitch } from '../InputSwitch'
import { InputTextArea } from '../InputTextArea'
import QueueSelect from '../QueueSelect'
import { useStyles } from './styles'
import { QueueDepartmentSelect } from '../QueueDepartmentSelect'

// Novo esquema de validação
const ClosingReasonSchema = Yup.object().shape({
  name: Yup.string()
    .required(i18n.t('closingReasonModal.form.nameRequired'))
    .min(3, 'Nome muito curto'),
  description: Yup.string().nullable(),
  isActive: Yup.boolean(),
  queueIds: Yup.array()
    .of(Yup.number())
    .min(0, 'Selecione pelo menos um departamento ou deixe vazio.')
    .required('Selecione pelo menos um departamento ou deixe vazio.')
})

const initialState = {
  name: '',
  description: '',
  isActive: true,
  queueIds: []
}

const ClosingReasonModal = ({
  open,
  onClose,
  closingReasonId,
  defaultQueueId
}) => {
  const classes = useStyles()
  const [closingReason, setClosingReason] = useState(initialState)
  const [botQueues, setBotQueues] = useState([])

  useEffect(() => {
    let isMounted = true

    const fetchBotQueues = async () => {
      try {
        const { data } = await api.get('/queue')
        const filteredQueues = data.filter(queue => queue.isBot)
        if (isMounted) {
          setBotQueues(filteredQueues)
        }
      } catch (error) {
        toastError(error)
      }
    }

    fetchBotQueues()

    return () => {
      isMounted = false // Cancela a atualização se o componente desmontar
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    const fetchClosingReason = async () => {
      if (!closingReasonId) {
        setClosingReason({
          ...initialState,
          queueIds: defaultQueueId ? [defaultQueueId] : []
        })
        return
      }
      try {
        const { data } = await api.get(`/closing-reasons/${closingReasonId}`)
        if (isMounted) {
          setClosingReason(prevState => ({
            ...prevState,
            ...data,
            queueIds: data.queues ? data.queues.map(q => q.id) : []
          }))
        }
      } catch (err) {
        toastError(err)
      }
    }
    if (open) {
      fetchClosingReason()
    }
    return () => {
      isMounted = false // Cancela a atualização se o componente desmontar
    }
  }, [closingReasonId, open, defaultQueueId])

  useEffect(() => {
    if (open && !closingReasonId && defaultQueueId) {
      setClosingReason(prevState => ({
        ...prevState,
        queueIds: [defaultQueueId]
      }))
    }
  }, [open, closingReasonId, defaultQueueId])

  // const fetchBotQueues = async () => {
  //   try {
  //     const { data } = await api.get('/queue')
  //     const botQueues = data.filter(queue => queue.isBot)
  //     setBotQueues(botQueues)
  //   } catch (error) {
  //     toastError(error)
  //   }
  // }

  const handleClose = () => {
    onClose()
    setClosingReason(initialState)
  }

  const handleSaveClosingReason = async values => {
    const botQueueIds = botQueues.map(queue => queue.id)
    const queueIds = [...new Set([...values.queueIds, ...botQueueIds])]
    try {
      if (closingReasonId) {
        await api.put(`/closing-reasons/${closingReasonId}`, {
          ...values,
          queueIds
        })
      } else {
        await api.post('/closing-reasons', { ...values, queueIds })
      }
      toast.success(i18n.t('closingReasonModal.success'))
      onClose(true)
      setClosingReason(initialState)
    } catch (err) {
      toastError(err)
    }
  }

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          {closingReasonId ? 'Alterar Assunto' : 'Novo Assunto'}
        </DialogTitle>
        <Formik
          initialValues={closingReason}
          enableReinitialize={true}
          validationSchema={ClosingReasonSchema}
          onSubmit={handleSaveClosingReason}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <InputField
                      label="Nome"
                      // label={i18n.t('closingReasonModal.form.name')}
                      type="text"
                      fullWidth
                      name="name"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputTextArea
                      label={i18n.t('closingReasonModal.form.description')}
                      type="text"
                      multiline
                      rows={4}
                      fullWidth
                      name="description"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {/* <QueueSelect
                      label="Departamentos Vinculados"
                      selectedQueueIds={values.queueIds}
                      onChange={selectedIds =>
                        setFieldValue('queueIds', selectedIds)
                      }
                      filterByIsBot={true}
                      includeAllOption={true}
                    /> */}

                    <QueueDepartmentSelect
                      label="Departamentos Vinculados"
                      placeholder="Selecione os departamentos"
                      includeAllOption={true}
                      showBots={false}
                      filterByIsBot={true}
                      isMulti={true}
                      disabled={isSubmitting}
                      value={
                        Array.isArray(values.queueIds)
                          ? values.queueIds.map(id => ({ id: Number(id) }))
                          : []
                      }
                      onChange={selectedDepartments => {
                        const ids = selectedDepartments
                          .map(d => Number(d.id))
                          .filter(Boolean)
                        setFieldValue('queueIds', ids)
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <InputSwitch
                      checked={values.isActive}
                      label={i18n.t('closingReasonModal.form.active')}
                      onChange={e =>
                        setFieldValue('isActive', e.target.checked)
                      }
                      size="small"
                      name="isActive"
                      color="primary"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <ButtonSecondary
                  onClick={handleClose}
                  disabled={isSubmitting}
                  label={i18n.t('closingReasonModal.buttons.cancel')}
                />
                <ButtonPrimary
                  type="submit"
                  disabled={isSubmitting || !values.name}
                  className={classes.btnWrapper}
                  label={
                    closingReasonId
                      ? i18n.t('closingReasonModal.buttons.okEdit')
                      : i18n.t('closingReasonModal.buttons.okAdd')
                  }
                />
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  )
}

export default ClosingReasonModal
